import { Base } from '@itsrever/design-system'
import { createContext, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

const {
  Button,
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogTitle,
  DialogDescription,
  DialogHeader,
  DialogFooter
} = Base

interface DialogOptions {
  title: string
  message?: React.ReactNode
  okButtonText?: string
  cancelButtonText?: string
  // An irreversible step: the confirm button is the destructive one.
  destructive?: boolean
}

interface PromiseInfo {
  resolve: (value: boolean | PromiseLike<boolean>) => void
  reject: (reason?: unknown) => void
}

type ShowDialogHandler = (options: DialogOptions) => Promise<boolean>

const DialogContext = createContext<ShowDialogHandler>(() => {
  throw new Error('Component is not wrapped with a DialogProvider.')
})

// A confirm dialog as a promise: `await showDialog({...})` resolves true on confirm and false on
// cancel, Escape or a click outside. Same dialog primitives as the rest of the dashboard, with
// the two answers as its only controls: a close cross would be a third way to say "cancel".
const DialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<DialogOptions>({ title: '' })
  const [promiseInfo, setPromiseInfo] = useState<PromiseInfo>()
  // Defaults resolve per dialog, so one dialog's labels never carry over into the next.
  const {
    message = t('dialog.are_you_sure'),
    okButtonText = t('dialog.continue'),
    cancelButtonText = t('save_bar.cancel')
  } = options

  const showDialog: ShowDialogHandler = (optionsArgs) => {
    return new Promise<boolean>((resolve, reject) => {
      setPromiseInfo({ resolve, reject })
      setOptions(optionsArgs)
      setOpen(true)
    })
  }

  const answer = (confirmed: boolean) => {
    setOpen(false)
    promiseInfo?.resolve(confirmed)
    setPromiseInfo(undefined)
  }

  return (
    <>
      <DialogRoot
        open={open}
        onOpenChange={(next) => {
          if (!next) answer(false)
        }}
      >
        <DialogPortal>
          <DialogBackdrop />
          <DialogPopup className="w-full max-w-md">
            <div className="flex flex-col gap-4">
              <DialogHeader>
                <DialogTitle>{options.title}</DialogTitle>
                {message && (
                  // A message that isn't plain text may hold blocks, which a <p> can't contain.
                  <DialogDescription
                    render={typeof message === 'string' ? undefined : <div />}
                  >
                    {message}
                  </DialogDescription>
                )}
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => answer(false)}
                >
                  {cancelButtonText}
                </Button>
                <Button
                  type="button"
                  variant={options.destructive ? 'destructive' : 'primary'}
                  onClick={() => answer(true)}
                >
                  {okButtonText}
                </Button>
              </DialogFooter>
            </div>
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>
      <DialogContext.Provider value={showDialog}>
        {children}
      </DialogContext.Provider>
    </>
  )
}

export const useDialog = () => {
  return useContext(DialogContext)
}

export default DialogProvider
